import {
  CollectionMetric,
  MemberLastActivity,
  TeamCollection,
  TeamMember,
} from './types'

const EXTRA_ENGAGED_NAMES = [
  'Chris Morgan',
  'Pat Rivera',
  'Jamie Ortiz',
  'Robin Hale',
  'Skyler Vance',
  'Dana Brooks',
  'Parker Quinn',
  'Reese Ames',
  'Taylor Knox',
  'Morgan Blake',
  'Casey Holt',
  'Avery Finch',
  'Jordan Shaw',
]

/** First 3 names are shown as avatars; remaining names appear in the +N tooltip. */
export const buildEngagedWith = (
  visibleNames: string[],
  totalCount: number
) => {
  const names = [...visibleNames]
  let extraIndex = 0
  while (names.length < totalCount) {
    const baseName =
      EXTRA_ENGAGED_NAMES[extraIndex % EXTRA_ENGAGED_NAMES.length]
    const cycle = Math.floor(extraIndex / EXTRA_ENGAGED_NAMES.length)
    names.push(cycle === 0 ? baseName : `${baseName} ${cycle + 1}`)
    extraIndex += 1
  }
  return names
}

const avatarFor = (id: string) => `https://i.pravatar.cc/150?u=${id}`

const DEFAULT_MEMBER_METRIC: CollectionMetric = {
  count: 21,
  amount: 200_000,
}

/** Exported for unit tests covering invalid ISO date formatting. */
export const formatActivityOn = (isoDate: string) => {
  const date = new Date(isoDate)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  const month = date.toLocaleDateString('en-US', { month: 'short' })
  return `${month} ${date.getDate()},${date.getFullYear()}`
}

type MemberSeed = Omit<TeamMember, 'metrics' | 'lastActivity' | 'avatarUrl'> & {
  avatarUrl?: string
  metrics?: TeamMember['metrics']
  lastActivity?: MemberLastActivity
}

/** Exported for unit tests covering createMember defaulting branches. */
export const createMember = (seed: MemberSeed): TeamMember => {
  const firstName = seed.name.split(' ')[0] || seed.name
  const activityAt = '2026-06-13T12:00:00.000Z'
  const engagedWith = seed.engagedWith ?? []
  const partnerFirstName = engagedWith[0]?.split(' ')[0] || 'Sophie'
  return {
    ...seed,
    avatarUrl: seed.avatarUrl ?? avatarFor(seed.id),
    engagedWith,
    location: seed.location ?? '',
    metrics: seed.metrics ?? {
      closedWon: DEFAULT_MEMBER_METRIC,
      inProgress: DEFAULT_MEMBER_METRIC,
    },
    lastActivity: seed.lastActivity ?? {
      at: activityAt,
      summary: `${partnerFirstName} performed an account mapping exercise with ${firstName} on ${formatActivityOn(activityAt)}`,
    },
  }
}

export type TeamRelationshipDummyResponse = {
  members: TeamMember[]
  collections: TeamCollection[]
  suggestedMemberIds: string[]
}

/**
 * Single temporary API-shaped payload until Team Relationship endpoints are wired.
 * One response populates members, collections (active + archived), and suggested picks.
 */
export const DUMMY_TEAM_RELATIONSHIP_RESPONSE: TeamRelationshipDummyResponse = {
  members: [
    createMember({
      id: 'ae-floyd-ironwel',
      name: 'Floyd Ironwel',
      role: 'Account Executive',
      location: 'New York',
      engagedWith: buildEngagedWith(
        ['Alex Kim', 'Jordan Lee', 'Sam Patel'],
        14
      ),
    }),
    createMember({
      id: 'ae-darrell-cooper',
      name: 'Darrell Cooper',
      role: 'GenAI Growth Account Director',
      location: 'New Mexico City',
      engagedWith: buildEngagedWith(
        ['Casey Ng', 'Riley Chen', 'Morgan Diaz'],
        14
      ),
    }),
    createMember({
      id: 'ae-jacob-jones',
      name: 'Jacob Jones',
      role: 'Account Executive',
      location: 'New York',
      engagedWith: buildEngagedWith(
        ['Taylor Brooks', 'Jamie Fox', 'Quinn Reed'],
        14
      ),
    }),
    createMember({
      id: 'ae-leslie-alexander',
      name: 'Leslie Alexander',
      role: 'Account Executive',
      location: '',
      engagedWith: buildEngagedWith(
        ['Avery Cole', 'Drew Hayes', 'Blake West'],
        14
      ),
    }),
    createMember({
      id: 'ae-esther-howard',
      name: 'Esther Howard',
      role: 'Enterprise AE',
      location: 'New York',
      engagedWith: buildEngagedWith(
        ['Cameron Ortiz', 'Reese Park', 'Harper Diaz'],
        9
      ),
    }),
    createMember({
      id: 'ae-jane-cooper',
      name: 'Jane Cooper',
      role: 'Strategic AE',
      location: 'Seattle',
      engagedWith: buildEngagedWith(
        ['Noah Grant', 'Ellie Shaw', 'Owen Price'],
        11
      ),
    }),
    createMember({
      id: 'ae-robert-fox',
      name: 'Robert Fox',
      role: 'Cloud Specialist',
      location: 'Austin',
      engagedWith: buildEngagedWith(
        ['Liam Scott', 'Mia Torres', 'Ethan Bell'],
        7
      ),
    }),
    createMember({
      id: 'ae-brooklyn-simmons',
      name: 'Brooklyn Simmons',
      role: 'Growth Manager',
      location: 'Chicago',
      engagedWith: buildEngagedWith(
        ['Sofia Lane', 'Lucas Hart', 'Zoe Miles'],
        12
      ),
    }),
    createMember({
      id: 'ae-wade-warren',
      name: 'Wade Warren',
      role: 'Partner AE',
      location: 'Boston',
      engagedWith: ['Nina Cole', 'Owen Blake'],
    }),
    createMember({
      id: 'ae-cameron-williamson',
      name: 'Cameron Williamson',
      role: 'GenAI Growth Manager',
      location: 'San Francisco',
      engagedWith: buildEngagedWith(
        ['Leo Dunn', 'Aria Wells', 'Kai Brooks'],
        16
      ),
    }),
    createMember({
      id: 'ae-jenny-wilson',
      name: 'Jenny Wilson',
      role: 'Mid-Market AE',
      location: 'Denver',
      engagedWith: ['Chris Morgan'],
    }),
    createMember({
      id: 'ae-albert-flores',
      name: 'Albert Flores',
      role: 'Enterprise AE',
      location: 'Miami',
      engagedWith: buildEngagedWith(
        ['Harper Diaz', 'Reese Park', 'Cameron Ortiz'],
        5
      ),
    }),
  ],
  collections: [
    {
      id: 'col-la-startup',
      name: 'Los Angeles Startup Team',
      type: 'strategic_team',
      memberIds: [
        'ae-floyd-ironwel',
        'ae-darrell-cooper',
        'ae-jacob-jones',
        'ae-leslie-alexander',
        'ae-esther-howard',
        'ae-jane-cooper',
        'ae-robert-fox',
        'ae-brooklyn-simmons',
      ],
      createdAt: '2026-06-13T12:00:00.000Z',
      createdBy: 'Darrell Cooper',
      metrics: {
        closedWon: { count: 40, amount: 200_000 },
        inProgress: { count: 40, amount: 200_000 },
        closedLost: { count: 40, amount: 200_000 },
      },
      isArchived: false,
    },
    {
      id: 'col-ny-startup',
      name: 'New York Startup Team',
      type: 'geography',
      memberIds: [
        'ae-floyd-ironwel',
        'ae-jacob-jones',
        'ae-leslie-alexander',
        'ae-robert-fox',
        'ae-cameron-williamson',
        'ae-esther-howard',
        'ae-jane-cooper',
        'ae-wade-warren',
      ],
      createdAt: '2026-06-13T12:00:00.000Z',
      createdBy: 'Darrell Cooper',
      metrics: {
        closedWon: { count: 45, amount: 0 },
        inProgress: { count: 0, amount: 563_000 },
        closedLost: { count: 0, amount: 0 },
      },
      isArchived: false,
    },
    {
      id: 'col-enterprise-segment',
      name: 'Enterprise Segment Pod',
      type: 'segment',
      memberIds: [
        'ae-darrell-cooper',
        'ae-esther-howard',
        'ae-jane-cooper',
        'ae-albert-flores',
      ],
      createdAt: '2026-04-18T15:45:00.000Z',
      createdBy: 'Leslie Alexander',
      metrics: {
        closedWon: { count: 12, amount: 1_250_000 },
        inProgress: { count: 8, amount: 890_000 },
        closedLost: { count: 3, amount: 210_000 },
      },
      isArchived: false,
    },
    {
      id: 'col-named-fortune500',
      name: 'Fortune 500 Named Accounts',
      type: 'named_accounts',
      memberIds: [
        'ae-esther-howard',
        'ae-jane-cooper',
        'ae-brooklyn-simmons',
        'ae-wade-warren',
        'ae-jenny-wilson',
        'ae-albert-flores',
      ],
      createdAt: '2026-03-21T11:00:00.000Z',
      createdBy: 'Jane Cooper',
      metrics: {
        closedWon: { count: 6, amount: 2_400_000 },
        inProgress: { count: 11, amount: 1_750_000 },
        closedLost: { count: 2, amount: 400_000 },
      },
      isArchived: false,
    },
    {
      id: 'col-midmarket-growth',
      name: 'Mid-Market Growth',
      type: 'segment',
      memberIds: ['ae-brooklyn-simmons', 'ae-jenny-wilson'],
      createdAt: '2026-02-10T08:15:00.000Z',
      createdBy: 'Brooklyn Simmons',
      metrics: {
        closedWon: { count: 22, amount: 180_000 },
        inProgress: { count: 14, amount: 120_000 },
        closedLost: { count: 7, amount: 45_000 },
      },
      isArchived: false,
    },
    {
      id: 'col-northeast-geo-archived',
      name: 'Northeast Territory (Archived)',
      type: 'geography',
      memberIds: ['ae-esther-howard', 'ae-wade-warren'],
      createdAt: '2025-11-04T14:20:00.000Z',
      createdBy: 'Wade Warren',
      metrics: {
        closedWon: { count: 15, amount: 320_000 },
        inProgress: { count: 0, amount: 0 },
        closedLost: { count: 11, amount: 140_000 },
      },
      isArchived: true,
    },
    {
      id: 'col-partner-pod-archived',
      name: 'Partner Pursuit Pod',
      type: 'strategic_team',
      memberIds: [
        'ae-darrell-cooper',
        'ae-wade-warren',
        'ae-cameron-williamson',
      ],
      createdAt: '2025-09-16T10:00:00.000Z',
      createdBy: 'Darrell Cooper',
      metrics: {
        closedWon: { count: 9, amount: 670_000 },
        inProgress: { count: 1, amount: 50_000 },
        closedLost: { count: 4, amount: 88_000 },
      },
      isArchived: true,
    },
    {
      id: 'col-legacy-named-archived',
      name: 'Legacy Named Accounts',
      type: 'named_accounts',
      memberIds: ['ae-floyd-ironwel', 'ae-robert-fox', 'ae-albert-flores'],
      createdAt: '2025-07-01T16:40:00.000Z',
      createdBy: 'Robert Fox',
      metrics: {
        closedWon: { count: 4, amount: 95_000 },
        inProgress: { count: 0, amount: 0 },
        closedLost: { count: 18, amount: 260_000 },
      },
      isArchived: true,
    },
  ],
  suggestedMemberIds: [
    'ae-floyd-ironwel',
    'ae-darrell-cooper',
    'ae-jacob-jones',
    'ae-leslie-alexander',
  ],
}

export const normalizeCollections = (collections: TeamCollection[]) => {
  const collectionsById: Record<string, TeamCollection> = {}
  const collectionIds: string[] = []
  for (const collection of collections) {
    collectionsById[collection.id] = collection
    collectionIds.push(collection.id)
  }
  return { collectionsById, collectionIds }
}

export const normalizeMembers = (members: TeamMember[]) => {
  const membersById: Record<string, TeamMember> = {}
  const memberIds: string[] = []
  for (const member of members) {
    membersById[member.id] = member
    memberIds.push(member.id)
  }
  return { membersById, memberIds }
}

/** Normalize the single dummy response into store-ready slices. */
export const normalizeTeamRelationshipResponse = (
  response: TeamRelationshipDummyResponse = DUMMY_TEAM_RELATIONSHIP_RESPONSE
) => ({
  ...normalizeMembers(response.members),
  ...normalizeCollections(response.collections),
  suggestedMemberIds: response.suggestedMemberIds,
})

/** Convenience aliases used by existing call sites / specs. */
export const DUMMY_TEAM_MEMBERS = DUMMY_TEAM_RELATIONSHIP_RESPONSE.members
export const DUMMY_TEAM_COLLECTIONS =
  DUMMY_TEAM_RELATIONSHIP_RESPONSE.collections
export const DUMMY_SUGGESTED_MEMBER_IDS =
  DUMMY_TEAM_RELATIONSHIP_RESPONSE.suggestedMemberIds
